import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiSettings, FiPlay, FiPause, FiTrendingUp, FiUsers, FiCoffee } = FiIcons;

function AutoCafe() {
  const { state, dispatch } = useGame();
  const [isRunning, setIsRunning] = useState(false);
  const [autoCustomers, setAutoCustomers] = useState([]);
  const [earnings, setEarnings] = useState(0);

  // Auto cafe worker efficiency based on upgrades
  const getWorkerEfficiency = () => {
    const baseEfficiency = 0.3; // 30% of player efficiency
    const machineBonus = state.cafeUpgrades.espressoMachine * 0.1;
    const decorationBonus = state.cafeUpgrades.decoration * 0.05;
    const levelBonus = state.level * 0.02;
    
    return Math.min(0.8, baseEfficiency + machineBonus + decorationBonus + levelBonus);
  };

  // Generate auto customers
  const generateAutoCustomer = () => {
    const simpleRecipes = ['espresso', 'americano', 'latte'];
    const availableRecipes = simpleRecipes.filter(recipe => 
      state.recipes.includes(recipe)
    );
    
    if (availableRecipes.length === 0) return null;
    
    const randomRecipe = availableRecipes[Math.floor(Math.random() * availableRecipes.length)];
    
    return {
      id: Date.now() + Math.random(),
      drink: randomRecipe,
      startTime: Date.now(),
      efficiency: getWorkerEfficiency()
    };
  };

  // Process auto customers
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      // Generate new customers
      if (autoCustomers.length < 2 && Math.random() < 0.4) {
        const newCustomer = generateAutoCustomer();
        if (newCustomer) {
          setAutoCustomers(prev => [...prev, newCustomer]);
        }
      }

      // Process existing customers
      setAutoCustomers(prev => {
        const now = Date.now();
        const updated = prev.map(customer => {
          const processingTime = now - customer.startTime;
          const requiredTime = 8000 / customer.efficiency; // Base 8 seconds
          
          if (processingTime >= requiredTime) {
            // Customer completed
            const baseEarning = 50;
            const earning = Math.floor(baseEarning * customer.efficiency);
            setEarnings(e => e + earning);
            
            dispatch({ type: 'ADD_COINS', payload: earning });
            dispatch({ type: 'ADD_EXPERIENCE', payload: Math.floor(earning / 5) });
            dispatch({ type: 'UPDATE_STATS', payload: {
              drinksServed: (state.stats.drinksServed || 0) + 1,
              totalEarnings: (state.stats.totalEarnings || 0) + earning
            }});
            
            return null; // Remove customer
          }
          
          return customer;
        }).filter(Boolean);
        
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, autoCustomers.length, state, dispatch]);

  const toggleAutoCafe = () => {
    setIsRunning(!isRunning);
    if (!isRunning) {
      setEarnings(0);
    }
  };

  const efficiency = getWorkerEfficiency();
  const maxCustomers = Math.min(4, Math.floor(state.level / 2) + 1);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <SafeIcon icon={FiSettings} className="text-purple-500 mr-2 text-lg" />
          <div>
            <h3 className="font-bold text-gray-800">Auto Cafe</h3>
            <p className="text-xs text-gray-500">AI workers serve customers</p>
          </div>
        </div>
        
        <motion.button
          className={`px-4 py-2 rounded-xl font-bold transition-all ${
            isRunning 
              ? 'bg-red-500 text-white' 
              : 'bg-green-500 text-white'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleAutoCafe}
        >
          <SafeIcon 
            icon={isRunning ? FiPause : FiPlay} 
            className="inline mr-1" 
          />
          {isRunning ? 'Stop' : 'Start'}
        </motion.button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-purple-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-purple-600">
            {Math.round(efficiency * 100)}%
          </div>
          <div className="text-xs text-purple-700">Efficiency</div>
        </div>
        
        <div className="bg-green-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-green-600">
            ${earnings}
          </div>
          <div className="text-xs text-green-700">Session</div>
        </div>
        
        <div className="bg-blue-50 p-2 rounded-lg text-center">
          <div className="text-lg font-bold text-blue-600">
            {autoCustomers.length}/{maxCustomers}
          </div>
          <div className="text-xs text-blue-700">Queue</div>
        </div>
      </div>

      {/* Active Customers */}
      {isRunning && autoCustomers.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Active Orders:</p>
          {autoCustomers.map(customer => {
            const progress = Math.min(100, 
              ((Date.now() - customer.startTime) / (8000 / customer.efficiency)) * 100
            );
            
            return (
              <div key={customer.id} className="bg-gray-50 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm capitalize">
                    {customer.drink.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-gray-500">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upgrade Hints */}
      {!isRunning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
          <div className="flex items-center mb-2">
            <SafeIcon icon={FiTrendingUp} className="text-amber-600 mr-2" />
            <span className="text-sm font-medium text-amber-800">
              Upgrade Tips
            </span>
          </div>
          <div className="text-xs text-amber-700 space-y-1">
            <p>• Better equipment = higher efficiency</p>
            <p>• Cafe decoration attracts more customers</p>
            <p>• Higher level = more simultaneous orders</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default AutoCafe;